<template>
  <div class="comments-section">
    <v-divider class="mb-2"></v-divider>

    <!-- Comments list -->
    <div
      v-if="comments.length"
      class="comments-list"
    >
      <div
        v-for="comment in comments"
        :key="comment.id"
        class="comment-item d-flex align-start pa-2"
      >
        <v-avatar
          size="32"
          class="mr-2 mt-1 cursor-pointer"
          @click="goToProfile(comment.user_username)"
        >
          <v-img
            v-if="comment.user_avatar"
            :src="comment.user_avatar"
          ></v-img>
          <v-icon
            v-else
            size="32"
          >
            {{ mdiAccountCircle }}
          </v-icon>
        </v-avatar>
        <div class="comment-body flex-grow-1">
          <div class="comment-bubble pa-2">
            <span
              class="comment-author font-weight-medium cursor-pointer"
              @click="goToProfile(comment.user_username)"
            >{{ comment.user_name }}</span>
            <div class="comment-text body-2">
              {{ comment.text }}
            </div>
          </div>
          <div class="comment-meta d-flex align-center mt-1">
            <small class="text--secondary">{{ formatTimeAgo(comment.created_at) }}</small>
            <v-btn
              v-if="canDelete(comment)"
              x-small
              text
              color="error"
              class="ml-2 delete-btn"
              @click="confirmDelete(comment)"
            >
              Delete
            </v-btn>
          </div>
        </div>
      </div>

      <div
        v-if="hasMore"
        class="text-center py-2"
      >
        <v-btn
          small
          text
          color="primary"
          :loading="loadingMore"
          @click="loadMore"
        >
          View more comments
        </v-btn>
      </div>
    </div>

    <div
      v-else-if="!loading"
      class="text-center py-2"
    >
      <small class="text--secondary">No comments yet</small>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      height="2"
      class="mb-2"
    ></v-progress-linear>

    <!-- Comment input -->
    <div
      v-if="isAuthenticated"
      class="comment-input d-flex align-center pa-2"
    >
      <v-avatar
        size="28"
        class="mr-2"
      >
        <v-img
          v-if="currentUserAvatar"
          :src="currentUserAvatar"
        ></v-img>
        <v-icon
          v-else
          size="28"
        >
          {{ mdiAccountCircle }}
        </v-icon>
      </v-avatar>
      <v-text-field
        v-model="newComment"
        placeholder="Write a comment..."
        dense
        outlined
        hide-details
        class="comment-field"
        maxlength="2000"
        @keyup.enter="submitComment"
      ></v-text-field>
      <v-btn
        icon
        small
        color="primary"
        :disabled="!newComment.trim()"
        :loading="submitting"
        class="ml-1"
        @click="submitComment"
      >
        <v-icon small>
          {{ mdiSend }}
        </v-icon>
      </v-btn>
    </div>

    <!-- Delete confirmation -->
    <v-dialog
      v-model="deleteDialog"
      max-width="320"
    >
      <v-card>
        <v-card-title class="text-h6">
          Delete comment?
        </v-card-title>
        <v-card-text>This action cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="deleteDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            text
            :loading="deleting"
            @click="deleteComment"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mdiSend, mdiAccountCircle } from '@mdi/js'

export default {
  name: 'CommentsSection',
  props: {
    fileId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      mdiSend,
      mdiAccountCircle,
      comments: [],
      newComment: '',
      loading: false,
      loadingMore: false,
      submitting: false,
      deleting: false,
      page: 1,
      totalPages: 1,
      commentCount: 0,
      endpoint: process.env.VUE_APP_ROOT_API,
      deleteDialog: false,
      commentToDelete: null,
    }
  },
  computed: {
    isAuthenticated() {
      return !!this.$store.state.jwtToken
    },
    currentUserAvatar() {
      return this.$store.getters.avatarUrl || null
    },
    currentUserId() {
      return this.$store.state.user && this.$store.state.user.extra_data
        ? this.$store.state.user.extra_data.id
        : null
    },
    hasMore() {
      return this.page < this.totalPages
    },
  },
  watch: {
    fileId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.resetAndLoad()
        }
      },
    },
  },
  methods: {
    resetAndLoad() {
      this.comments = []
      this.page = 1
      this.totalPages = 1
      this.commentCount = 0
      this.fetchComments()
    },
    async fetchComments() {
      if (!this.fileId) return
      this.loading = true
      try {
        const response = await axios.get(
          `${this.endpoint}/getComments?file_id=${this.fileId}&page=${this.page}&limit=20`,
          { headers: this.$store.state.jwtToken ? { Authorization: this.$store.state.jwtToken } : {} },
        )
        if (response.data) {
          if (this.page === 1) {
            this.comments = response.data.comments || []
          } else {
            this.comments = [...this.comments, ...(response.data.comments || [])]
          }
          this.commentCount = response.data.commentCount || 0
          this.totalPages = response.data.totalPages || 1
          this.$emit('count-updated', this.commentCount)
        }
      } catch {
        // Silently fail
      }
      this.loading = false
    },
    async loadMore() {
      this.loadingMore = true
      this.page++
      await this.fetchComments()
      this.loadingMore = false
    },
    async submitComment() {
      const text = this.newComment.trim()
      if (!text || this.submitting) return

      this.submitting = true
      try {
        const response = await axios.post(
          `${this.endpoint}/addComment`,
          { file_id: this.fileId, text },
          { headers: { Authorization: this.$store.state.jwtToken } },
        )
        if (response.data && response.data.comment) {
          this.comments.push(response.data.comment)
          this.commentCount = response.data.commentCount || this.commentCount + 1
          this.newComment = ''
          this.$emit('count-updated', this.commentCount)
        }
      } catch {
        // Silently fail
      }
      this.submitting = false
    },
    canDelete(comment) {
      if (!this.currentUserId) return false

      return String(comment.user_id) === String(this.currentUserId)
    },
    confirmDelete(comment) {
      this.commentToDelete = comment
      this.deleteDialog = true
    },
    async deleteComment() {
      if (!this.commentToDelete) return
      this.deleting = true
      try {
        const response = await axios.post(
          `${this.endpoint}/deleteComment`,
          { comment_id: this.commentToDelete.id },
          { headers: { Authorization: this.$store.state.jwtToken } },
        )
        if (response.data && response.data.deleted) {
          this.comments = this.comments.filter(c => c.id !== this.commentToDelete.id)
          this.commentCount = response.data.commentCount || Math.max(0, this.commentCount - 1)
          this.$emit('count-updated', this.commentCount)
        }
      } catch {
        // Silently fail
      }
      this.deleting = false
      this.deleteDialog = false
      this.commentToDelete = null
    },
    goToProfile(username) {
      if (username) {
        this.$router.push(`/user/${username}`)
      }
    },
    formatTimeAgo(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) return `${diffHours}h ago`
      const diffDays = Math.floor(diffHours / 24)
      if (diffDays < 7) return `${diffDays}d ago`
      const diffWeeks = Math.floor(diffDays / 7)
      if (diffWeeks < 4) return `${diffWeeks}w ago`

      return date.toLocaleDateString()
    },
  },
}
</script>

<style scoped lang="scss">
.comments-section {
  max-height: 400px;
  overflow-y: auto;
}

.comments-list {
  max-height: 280px;
  overflow-y: auto;
}

.comment-item {
  &:hover {
    background: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
  }
}

.comment-bubble {
  background: #f0f2f5;
  border-radius: 18px;
  display: inline-block;
  max-width: 100%;
}

.comment-author {
  font-size: 13px;
  display: block;
}

.comment-text {
  font-size: 14px;
  word-break: break-word;
  white-space: pre-wrap;
}

.comment-meta {
  padding-left: 12px;

  .delete-btn {
    min-width: auto;
    padding: 0 4px;
    height: 20px;
    text-transform: none;
    font-size: 12px;
  }
}

.comment-field {
  border-radius: 20px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
